<template>
  <template v-if="hasContent">
    <!-- Header of the group -->
    <li 
      v-if="showHeader"
      :class="['structure-header', { 'subgroup-header': depth > 0 }]"
      :style="{ paddingLeft: headerPaddingLeft }"
      @mousedown.stop.prevent="handleHeaderClick"
    >
      <span class="header-text">
        {{ headerText }}
      </span>
      <span class="material-symbols-outlined expand-icon">
        {{ isCollapsed ? 'expand_more' : 'expand_less' }}
      </span>
    </li>
    
    <!-- Group content (modules & subgroups) if not collapsed -->
    <template v-if="!isCollapsed">
      <!-- Render modules directly in this group -->
      <li 
        v-for="module in (node.modules || [])" 
        :key="module.id"
        :class="['suggestion-item', { 'inactive': node.isUnavailable || module.isUnavailable }]"
        :style="{ paddingLeft: itemPaddingLeft }"
        @mousedown.stop.prevent="handleModuleClick($event, module)"
        @mouseenter="$emit('hover-module', module.id)"   
        @mouseleave="$emit('hover-module', null)"
        :title="getModuleTooltip(module)"
      >
        <div v-if="showName" class="module-name-bar">
          {{ module.name }}
          <span v-if="node.isUnavailable || module.isUnavailable" class="list-detail">
            {{ getModuleUnavailableReasonText(module) }}
          </span>
        </div>

        <div class="list-details">
          <div class="list-detail list-detail-num">{{ module.mock || module.suppressId ? '' : module.number }}</div>

          <div v-if="showLanguage" class="list-detail list-detail-lang">
              {{ module.languages.sort().join('/') }}
          </div>

          <div v-if="showECTS && module.ects != 0" class="list-detail list-detail-ects">
              {{ module.ects }} ECTS
          </div>
          <div class="material-symbols-outlined info-icon" @click.stop="() => {}">info</div>
        </div>
      </li>

      <!-- Recursively render subgroups -->
      <DropdownNode
        v-for="subgroup in node.subgroups"
        :key="getGroupId(subgroup)"
        :node="subgroup"
        :depth="depth + 1"
        :collapsedGroups="collapsedGroups"
        :showName="showName"
        :showLanguage="showLanguage"
        :showECTS="showECTS"
        :unavailableReasonText="unavailableReasonText"
        :unavailableReason="unavailableReason"
        :currentSemester="currentSemester"
        @toggle-group="id => $emit('toggle-group', id)"
        @select-module="(event, module) => $emit('select-module', event, module)"
        @info-click="module => $emit('info-click', module)"
        @hover-module="moduleId => $emit('hover-module', moduleId)"
      />
    </template>
  </template>
</template>

<script>
import { getExplanatoryUnavailableReason } from '@/utils';

export default {
  name: "DropdownNode",
  props: {
    node: {
      type: Object,
      required: true
    },
    depth: {
      type: Number,
      default: 0
    },
    collapsedGroups: {
      type: Object,
      required: true
    },
    showName: {
      type: Boolean,
      default: true
    },
    showLanguage: {
      type: Boolean,
      default: true
    },
    showECTS: {
      type: Boolean,
      default: true
    },
    unavailableReasonText: {
      type: String,
      required: true
    },
    unavailableReason: {
      type: String,
      required: true
    },
    currentSemester: {
      type: [Number, String],
      default: null
    }
  },
  emits: ['toggle-group', 'select-module', 'info-click', 'hover-module'],
  computed: {
    hasContent() {
      return this.node.structure || this.node.isUnavailable || (this.node.modules && this.node.modules.length > 0) || (this.node.subgroups && this.node.subgroups.length > 0);
    },
    showHeader() {
      return !!(this.node.structure || this.node.isUnavailable);
    },
    headerText() {
      if (this.node.isUnavailable) {
        return 'Unselectable modules';
      }
      if (!this.node.structure) {
        return 'All available modules';
      }
      
      if (this.depth > 0) {
        return this.node.structure.name || '';
      }

      let name = this.node.structure.name || '';
      if (this.node.structure.isExtra || this.node.structure.isCustomName) {
        return name;
      }
      return `Modules part of ${name}`;
    },
    groupId() {
      return this.getGroupId(this.node);
    },
    isCollapsed() {
      return this.collapsedGroups[this.groupId] || false;
    },
    headerPaddingLeft() {
      if (this.depth === 0) return '16px !important';
      return `${25 + (this.depth - 1) * 12}px !important`;
    },
    itemPaddingLeft() {
      return `${24 + this.depth * 12}px !important`;
    }
  },
  methods: {
    getGroupId(node) {
      if (node.isUnavailable) return 'structure-unavailable';
      if (!node.structure) return 'no-structure';
      return `structure-${node.structure.id}`;
    },
    handleHeaderClick() {
      this.$emit('toggle-group', this.groupId);
    },
    handleModuleClick(event, module) {
      if (event.target.classList.contains('info-icon')) {
        this.$emit('info-click', module);
      } else if (!this.node.isUnavailable && !module.isUnavailable) {
        this.$emit('select-module', event, module);
      }
    },
    getModuleUnavailableReasonObj(module) {
      const startSem = this.$store?.state?.preferences?.startSemester;
      return getExplanatoryUnavailableReason(module, this.unavailableReason, this.currentSemester, startSem);
    },
    getModuleUnavailableReasonText(module) {
      return `(${this.getModuleUnavailableReasonObj(module).short})`;
    },
    getModuleTooltip(module) {
      if (!this.node.isUnavailable && !module.isUnavailable) {
        return module.name;
      }
      const reasonObj = this.getModuleUnavailableReasonObj(module);
      return `${module.name} (${reasonObj.short})`;
    }
  }
};
</script>

<style scoped>
.structure-header {
  background-color: #fff8f2;
  color: #e65100;
  font-weight: 600;
  padding: 10px 16px !important;
  cursor: pointer !important;
  border-bottom: 1px solid #f0f0f0 !important;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  user-select: none;
  box-sizing: border-box;
}

.structure-header:hover {
  background-color: #ffebd8;  
}

.subgroup-header {
  background-color: #fff8f2;
  color: #e65100;
  font-weight: 500;
  border-top: 1px solid #f0f0f0;
  box-sizing: border-box;
}

.subgroup-header:hover {
  background-color: #ffebd8;
}

.header-text {
  flex-grow: 1;
}

.expand-icon {
  color: #e65100;
  font-size: 20px;
  margin-left: 8px;
  transition: transform 0.2s ease;
  pointer-events: none; 
}

.suggestion-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  justify-content: space-between;
  padding: 5px 10px;
  cursor: pointer;
  border-bottom: 1px solid #f1f1f1;
  box-sizing: border-box;
}

.suggestion-item.inactive {
  color: #666;
  cursor: inherit;
  background-color: #f9f9f9 !important;
}

.suggestion-item:not(.inactive):hover {
  background-color: #f1f1f1 !important; 
}

.inactive .list-detail {
  color: inherit;
}

.list-details {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
  flex-shrink: 0; 
}

.list-detail:last-child {
  padding-right: 10px;
}

.list-detail {
  color: #333333;
  font-size: 80%;
  text-align: right;
  padding-top: 2px;
}

.list-detail-lang,
.list-detail-ects {
  width: 50px;
}

.list-detail-num {
  white-space: nowrap;
  color: #333;
}

.module-name-bar {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  flex: 1;
}

.info-icon {
  font-size: 20px;
  color: #666;
  cursor: pointer;
}

.info-icon:hover {
  color: #333;
}
</style>
